/**
 * Authentication helper utilities for Security Analysis and other components.
 * Provides consistent patterns for auth state, signup redirects, and API access.
 */
import {
  getDevSubscriptionTier,
  resolveSubscriptionEntitlements,
} from './subscriptionEntitlements';
import { AUTH0_AUDIENCE, AUTH_VALIDATE_URL, CDN_BASE_URL } from '../constants/api';

// ============================================
// Session Expiry Detection
// ============================================

let sessionExpiredFired = false;

/**
 * Dispatch the auth:session-expired custom event (debounced — fires at most once per page load).
 * Any component or hook can call this; App.js listens and triggers logout.
 */
export const handleSessionExpired = () => {
  if (sessionExpiredFired) return;
  sessionExpiredFired = true;
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
};

// ============================================
// API Configuration
// ============================================

/** Base API Gateway URL for authenticated requests */
export const API_URL = AUTH_VALIDATE_URL;

/** CloudFront URL for public/free data */
export const CF_URL = CDN_BASE_URL;

// ============================================
// Auth State Helpers
// ============================================

/**
 * Get normalized authentication state with development overrides
 * @param {Object} auth0State - State from useAuth0 hook
 * @param {Object} options - Configuration options
 * @param {boolean} options.devIsPaid - Whether to treat as paid in dev mode (default: true)
 * @param {boolean} options.devIsAuthenticated - Whether to treat as authenticated in dev mode (default: true)
 * @returns {Object} Normalized auth state { user, isAuthenticated, isPaid, subStatus }
 */
export const getAuthState = (auth0State, options = {}) => {
  const { user, isAuthenticated: authIsAuthenticated, getAccessTokenSilently, loginWithRedirect } = auth0State;
  const { devIsAuthenticated = true } = options;
  const entitlements = resolveSubscriptionEntitlements(user, {
    devSubscriptionTier: getDevSubscriptionTier(),
  });
  const subStatus = entitlements.subscriptionStatus;
  const isPaid = entitlements.hasCoreAccess;
  const isAuthenticated = process.env.NODE_ENV === 'development'
    ? devIsAuthenticated
    : authIsAuthenticated;

  return {
    user,
    isAuthenticated,
    isPaid,
    hasCoreAccess: entitlements.hasCoreAccess,
    hasResearchAccess: entitlements.hasResearchAccess,
    subscriptionTier: entitlements.subscriptionTier,
    subStatus,
    getAccessTokenSilently,
    loginWithRedirect,
  };
};

/**
 * Check if paywall gate should be active
 * @param {boolean} isPaid - Whether user has paid subscription
 * @param {string} ticker - Current ticker (some tickers may be free)
 * @param {string[]} freeTickers - List of tickers that don't require payment (default: ['MDB', 'PVT-DBRK'])
 * @returns {boolean} True if paywall should be shown
 */
export const isGateActive = (isPaid, ticker, freeTickers = ['MDB', 'PVT-DBRK']) => {
  return !isPaid && !freeTickers.includes(String(ticker).toUpperCase());
};

// ============================================
// Signup/Login Redirect Helpers
// ============================================

/**
 * Create a signup redirect handler
 * @param {Object} auth0State - State from useAuth0 hook containing loginWithRedirect and isAuthenticated
 * @param {Object} options - Configuration options
 * @param {string} options.redirectTo - Path to redirect after auth (default: '')
 * @returns {Function} Handler function for signup button
 */
export const createSignupHandler = (auth0State, options = {}) => {
  const { isAuthenticated, loginWithRedirect } = auth0State;
  const { redirectTo = '' } = options;

  return () => {
    localStorage.setItem('redirectTo', redirectTo);

    if (!isAuthenticated) {
      localStorage.setItem('requestedTier', 'trial');
      loginWithRedirect({
        appState: { requestedTier: 'trial' },
        authorizationParams: { screen_hint: 'signup', scope: 'openid email profile' },
      });
    } else {
      localStorage.setItem('requestedTier', 'pay');
      loginWithRedirect({
        appState: { requestedTier: 'pay' },
        authorizationParams: { screen_hint: 'signup', scope: 'openid email profile' },
      });
    }
  };
};

/**
 * Get CTA label based on auth state
 * @param {boolean} isAuthenticated - Whether user is logged in
 * @returns {string} CTA button label
 */
export const getCtaLabel = (isAuthenticated) => {
  return isAuthenticated ? 'Upgrade to Core' : 'Start 7-day Trial';
};

// ============================================
// API Token Helper
// ============================================

/**
 * Get API access token with error handling
 * @param {Function} getAccessTokenSilently - Auth0 token function
 * @param {Object} options - Token request options
 * @returns {Promise<string|null>} Access token or null on failure
 */
export const getApiToken = async (getAccessTokenSilently, options = {}) => {
  const {
    audience = AUTH0_AUDIENCE,
    scope = 'read:news',
  } = options;

  try {
    return await getAccessTokenSilently({ audience, scope });
  } catch (error) {
    console.error('Error getting access token:', error);

    if (error.message?.includes('Missing Refresh Token') || error.error === 'login_required') {
      handleSessionExpired();
      return null;
    }

    throw error;
  }
};

// ============================================
// Authenticated Fetch Helper
// ============================================

/**
 * Fetch data from authenticated API endpoint
 * @param {string} fileKey - File key to fetch
 * @param {Function} getAccessTokenSilently - Auth0 token function
 * @returns {Promise<Object>} Response data
 */
export const fetchAuthenticatedData = async (fileKey, getAccessTokenSilently) => {
  const token = await getApiToken(getAccessTokenSilently);

  if (!token) {
    throw new Error('Unable to get access token');
  }

  const response = await fetch(`${API_URL}?fileKey=${fileKey}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleSessionExpired();
    }
    throw new Error(`API request failed: ${response.status}`);
  }

  const result = await response.json();
  return result.data || result;
};

/**
 * Fetch data from public CloudFront endpoint
 * @param {string} fileName - File name to fetch
 * @returns {Promise<Object>} Response data
 */
export const fetchPublicData = async (fileName) => {
  const response = await fetch(`${CF_URL}/${fileName}`);

  if (!response.ok) {
    throw new Error(`Public data fetch failed: ${response.status}`);
  }

  return response.json();
};
